import { Controller } from "@hotwired/stimulus"
import { vendorSearch, vendorAdd, vendorGet } from "vendor_library"
import {
  resultsListHtml, selectedDisplayHtml, inputHtml, esc
} from "vendor_picker_view"

// Vendor Picker Controller — a combobox-style input that searches the local
// vendor library and allows inline creation of new vendors. Returns a vendor
// id; consumers render details from the library.
//
// Usage:
//   <div data-controller="vendor-picker"
//        data-vendor-picker-value-value="<id|''>"
//        data-vendor-picker-name-value="fieldName">
//
// Fires:
//   - vendor-picker:change with { id, vendor } on select/clear
//   - vendor-picker:added with { id, vendor } when a new vendor is created inline
export default class extends Controller {
  static values = {
    value: String,       // selected vendor id
    name: String,        // form field name for hidden input
    placeholder: { type: String, default: "Search vendors..." }
  }

  connect() {
    this._activeIndex = -1
    this._results = []
    this._open = false
    this.render()
    this._onClickOutside = (e) => {
      if (this._open && !this.element.contains(e.target)) this._close()
    }
    document.addEventListener("click", this._onClickOutside, true)
  }

  disconnect() {
    document.removeEventListener("click", this._onClickOutside, true)
  }

  render() {
    const vendorId = this.valueValue || ""
    const vendor = vendorId ? vendorGet(vendorId) : null
    const hasSelection = !!(vendorId && vendor)

    // Build wrapper HTML
    let html = `<div class="vendor-picker__wrapper">`
    if (hasSelection) {
      html += selectedDisplayHtml(vendor)
    } else {
      html += inputHtml(this.placeholderValue)
    }
    html += `<input type="hidden" name="${esc(this.nameValue || "")}" value="${esc(vendorId)}" data-vendor-picker-target="hidden">`
    html += `</div>`

    this.element.innerHTML = html
    this._wrapper = this.element.querySelector(".vendor-picker__wrapper")

    // Wire up input events if in search mode
    const input = this.element.querySelector(".vendor-picker__input")
    if (input) {
      this._input = input
      input.addEventListener("input", () => this._onInput())
      input.addEventListener("keydown", (e) => this._onKeydown(e))
      input.addEventListener("focus", () => this._onFocus())
    } else {
      this._input = null
    }

    // Wire up clear button
    const clearBtn = this.element.querySelector(".vendor-picker__clear-btn")
    if (clearBtn) {
      clearBtn.addEventListener("click", (e) => {
        e.preventDefault()
        this.clear()
      })
    }

    this._hiddenInput = this.element.querySelector('input[type="hidden"]')
  }

  // ---- Input event handlers ----

  _onFocus() {
    const query = (this._input?.value || "").trim()
    if (query.length >= 1) {
      this._search(query)
    }
  }

  _onInput() {
    const query = (this._input?.value || "").trim()
    if (query.length >= 1) {
      this._search(query)
    } else {
      this._close()
    }
  }

  _onKeydown(e) {
    if (!this._open) {
      // Open dropdown on any printable char if not already open
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        // Will open via input event
        return
      }
      return
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault()
        this._moveActive(1)
        break
      case "ArrowUp":
        e.preventDefault()
        this._moveActive(-1)
        break
      case "Enter":
        e.preventDefault()
        this._selectActive()
        break
      case "Escape":
        e.preventDefault()
        this._close()
        break
    }
  }

  _moveActive(delta) {
    const total = this._results.length + 1 // +1 for add row
    let next = this._activeIndex + delta
    if (next < 0) next = total - 1
    if (next >= total) next = 0
    this._activeIndex = next
    this._renderResults()
  }

  _selectActive() {
    if (this._activeIndex < 0) return
    if (this._activeIndex < this._results.length) {
      // Select existing vendor
      const vendor = this._results[this._activeIndex]
      this._selectVendor(vendor.id, vendor)
    } else {
      // Add new vendor
      this._addNewVendor()
    }
  }

  // ---- Search and results ----

  _search(query) {
    // Search by name, service types, zip (up to 8 results)
    this._results = vendorSearch(query).slice(0, 8)
    this._activeIndex = this._results.length > 0 ? 0 : 0 // always have add row
    this._query = query
    this._open = true
    this._renderResults()
    this._updateAriaExpanded(true)
  }

  _renderResults() {
    // Remove existing dropdown
    const existing = this.element.querySelector(".vendor-picker__results")
    if (existing) existing.remove()

    if (!this._open) return

    const html = resultsListHtml({
      results: this._results,
      query: this._query || "",
      activeIndex: this._activeIndex,
      maxResults: 8
    })

    this._wrapper.insertAdjacentHTML("beforeend", html)
    const dropdown = this.element.querySelector(".vendor-picker__results")
    if (dropdown) {
      dropdown.addEventListener("click", (e) => this._onResultClick(e))
      // Update aria-activedescendant
      if (this._input && this._activeIndex >= 0) {
        this._input.setAttribute("aria-activedescendant", `vp-opt-${this._activeIndex}`)
      }
    }
  }

  _onResultClick(e) {
    const row = e.target.closest(".vendor-picker__result")
    if (!row) return

    if (row.dataset.add === "true") {
      this._addNewVendor()
    } else {
      const vendorId = row.dataset.vendorId
      const vendor = vendorGet(vendorId)
      if (vendor) this._selectVendor(vendorId, vendor)
    }
  }

  _close() {
    this._open = false
    this._results = []
    this._activeIndex = -1
    const dropdown = this.element.querySelector(".vendor-picker__results")
    if (dropdown) dropdown.remove()
    this._updateAriaExpanded(false)
  }

  _updateAriaExpanded(expanded) {
    if (this._input) {
      this._input.setAttribute("aria-expanded", String(expanded))
    }
  }

  // ---- Selection ----

  _selectVendor(id, vendor) {
    this.valueValue = id
    this._close()
    this.render()
    this._dispatchChange(id, vendor)
  }

  clear() {
    this.valueValue = ""
    this.render()
    this._dispatchChange("", null)
    // Focus the newly rendered input
    setTimeout(() => {
      const input = this.element.querySelector(".vendor-picker__input")
      if (input) input.focus()
    }, 0)
  }

  _addNewVendor() {
    const name = (this._query || "").trim()
    if (!name) return

    // Create vendor with just the name
    const id = vendorAdd({ name })
    const vendor = vendorGet(id)

    this.valueValue = id
    this._close()
    this.render()

    // Dispatch both events: added (so host can open full editor) and change
    this._dispatchAdded(id, vendor)
    this._dispatchChange(id, vendor)
  }

  // ---- Events ----

  _dispatchChange(id, vendor) {
    // Update hidden input
    if (this._hiddenInput) {
      this._hiddenInput.value = id || ""
    }

    this.element.dispatchEvent(new CustomEvent("vendor-picker:change", {
      bubbles: true,
      detail: { id: id || "", vendor: vendor || null }
    }))
  }

  _dispatchAdded(id, vendor) {
    this.element.dispatchEvent(new CustomEvent("vendor-picker:added", {
      bubbles: true,
      detail: { id, vendor }
    }))
  }
}
